import React, { FC, useContext, useMemo } from "react";
import { TestType } from "@app/provider/testData/types";
import { defineMessages, useIntl } from "react-intl";
import { IntlContext } from "@app/provider";

const messages = defineMessages({
  testRenasightDescription: {
    id: "testRenasightDescription",
    defaultMessage: "Kidney Gene Panel",
  },
  testProsperaDescription: {
    id: "testProsperaDescription",
    defaultMessage: "Transplant Assessment",
  },
  testHorizonDescription: {
    id: "testHorizonDescription",
    defaultMessage: "Carrier Screening",
  },
  testPanoramaDescription: {
    id: "testPanoramaDescription",
    defaultMessage: "Non-Invasive Prenatal Testing-NIPT",
  },
  testEmpowerDescription: {
    id: "testEmpowerDescription",
    defaultMessage: "Hereditary Cancer Test",
  },
  testVistaraDescription: {
    id: "testVistaraDescription",
    defaultMessage: "Single-Gene NIPT",
  },
  testSGNIPTDescription: {
    id: "testSGNIPTDescription",
    defaultMessage: "NIPT for inherited conditions",
  },
  testAlteraDescription: {
    id: "testAlteraDescription",
    defaultMessage: "Tumor Genomic Profile Test",
  },
  testSignateraDescription: {
    id: "testSignateraDescription",
    defaultMessage: "Molecular Residual Disease Test",
  },
  testLatitudeDescription: {
    id: "testLatitudeDescription",
    defaultMessage: "Tissue-Free Molecular Residual Disease Test",
  },
  testDpydDescription: {
    id: "testDpydDescription",
    defaultMessage: "Biomarker Testing",
  },
});

interface Props {
  testType: TestType;
}

/**
 * Returns a map of localized, human-readable test descriptions keyed by
 * `TestType`. Exposed as a hook so other surfaces (e.g. the GIS order picker)
 * can reuse the same translated copy instead of duplicating it.
 */
export const useTestDescriptions = (): { [key: string]: string } => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);

  return useMemo(
    () => ({
      [TestType.RENASIGHT]: intl.formatMessage(
        messages.testRenasightDescription
      ),
      [TestType.PROSPERA]: intl.formatMessage(messages.testProsperaDescription),
      [TestType.HORIZON]: intl.formatMessage(messages.testHorizonDescription),
      [TestType.PANORAMA]: intl.formatMessage(messages.testPanoramaDescription),
      [TestType.EMPOWER]: intl.formatMessage(messages.testEmpowerDescription),
      [TestType.VISTARA]: intl.formatMessage(messages.testVistaraDescription),
      [TestType.SINGLE_GENE_NIPT]: intl.formatMessage(
        messages.testSGNIPTDescription
      ),
      [TestType.ALTERA]: intl.formatMessage(messages.testAlteraDescription),
      [TestType.SIGNATERA]: intl.formatMessage(
        messages.testSignateraDescription
      ),
      [TestType.LATITUDE]: intl.formatMessage(messages.testLatitudeDescription),
      [TestType.DPYD]: intl.formatMessage(messages.testDpydDescription),
    }),
    [intl, currentLanguage]
  );
};

const TestDescription: FC<Props> = ({ testType }) => {
  const descriptions = useTestDescriptions();

  return <>{descriptions[testType] || ""}</>;
};

export default TestDescription;
